import { memo, useCallback, useState } from "react";
import { parseISO } from "date-fns";
import { HugeiconsIcon } from "@hugeicons/react";
import { Calendar03Icon } from "@hugeicons/core-free-icons";
import { cn } from "@/lib/utils";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { usePeriodControls } from "../hooks/useOrderAnalytics";
import { useOrderAnalyticsStore } from "../stores/orderAnalyticsStore";
import { PERIOD_PRESETS, toDateString, validatePeriod } from "../utils/period";

interface DateFieldProps {
  label: string;
  value: string;
  disabled: boolean;
  onChange: (value: string) => void;
}

const DateField = memo(function DateField({
  label,
  value,
  disabled,
  onChange,
}: DateFieldProps) {
  const [isOpen, setIsOpen] = useState(false);
  const selected = parseISO(value);

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger
        disabled={disabled}
        aria-label={label}
        className="flex h-7 shrink-0 items-center gap-1.5 rounded-md border border-gray-300 bg-white px-2 text-xs whitespace-nowrap text-gray-700 tabular-nums outline-none hover:bg-gray-50 focus-visible:border-blue-500 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <HugeiconsIcon icon={Calendar03Icon} size={14} className="text-gray-400" />
        {value}
      </PopoverTrigger>
      <PopoverContent align="end" className="w-auto p-0">
        <Calendar
          mode="single"
          required
          selected={selected}
          defaultMonth={selected}
          disabled={{ after: new Date() }}
          onSelect={(date) => {
            onChange(toDateString(date));
            setIsOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
});

interface PeriodSelectorProps {
  /** 모두 가져오기 진행 중에는 기간을 바꿀 수 없다 */
  disabled: boolean;
}

export const PeriodSelector = memo(function PeriodSelector({
  disabled,
}: PeriodSelectorProps) {
  const period = useOrderAnalyticsStore((s) => s.period);
  const presetKey = useOrderAnalyticsStore((s) => s.presetKey);
  const { applyPreset, changePeriod } = usePeriodControls();
  const error = validatePeriod(period);

  const handleStartChange = useCallback(
    (startDate: string) => changePeriod({ ...period, startDate }),
    [changePeriod, period],
  );
  const handleEndChange = useCallback(
    (endDate: string) => changePeriod({ ...period, endDate }),
    [changePeriod, period],
  );

  return (
    <div className="flex flex-col items-end gap-1">
      <div className="flex items-center gap-1.5">
        {PERIOD_PRESETS.map(({ key, label }) => (
          <button
            key={key}
            type="button"
            disabled={disabled}
            aria-pressed={presetKey === key}
            onClick={() => applyPreset(key)}
            className={cn(
              "h-7 rounded-md border px-2.5 text-xs transition-colors disabled:cursor-not-allowed disabled:opacity-50",
              presetKey === key
                ? "border-gray-800 bg-gray-800 font-medium text-white"
                : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50",
            )}
          >
            {label}
          </button>
        ))}
        <div className="ml-1.5 flex items-center gap-1">
          <DateField
            label="시작일"
            value={period.startDate}
            disabled={disabled}
            onChange={handleStartChange}
          />
          <span className="text-xs text-gray-400">~</span>
          <DateField
            label="종료일"
            value={period.endDate}
            disabled={disabled}
            onChange={handleEndChange}
          />
        </div>
      </div>
      {error && (
        <p role="alert" className="text-xs text-red-600">
          {error} 기간을 고쳐야 조회할 수 있습니다.
        </p>
      )}
    </div>
  );
});
